import request from "../../service/request";
import constants from "../constants";
import storageConstants from "../../shared/constants/storage";
import { getLocalStorage } from "../../shared/utils/localStorage";
import { getSessionStorage } from "../../shared/utils/sessionStorage";
import { setCookie } from "../utils/cookie";
import { logDTEvent } from "../../shared/analytics/dynatrace";
import { DT_EVENTS, DT_ATTRIBUTES } from "../../shared/constants/dynatrace";
import { ANALYTICS_STORAGE_KEYS } from "../../shared/constants/analytics";
import { isExpiryFeatureEnabled, updateExpiryFromResultObj } from "../utils/tokenExpiryHelpers";

const { APP_ENVIRONMENTS, API_BASE_URL, LOGIN_BRANDS, ALTERNATE_IDENTITY_PROVIDER, LOGIN_METHODS } = constants;
const { FPID, DEVICE_ID, CACHED_ACCOUNT_BRAND } = storageConstants;
const ENV = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;

/**
 * Authenticate a user using the alternate login API (INTERNAL/USER/SESSIONS)
 * @param {Object} appProvider
 * @param {String} email
 * @param {String} password
 * @returns {Promise}
 */
const alternateLogin = (appProvider, email, password, isDynatraceTrackingEnabled) => {
  let url = appProvider.config.avs_api_urls.alternate_login;
  let redirectUri = appProvider.config.general.AVS_config.redirectUri;

  url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
  url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
  url = url.replace("$LANG", appProvider.AVS_LANG);
  url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);

  if (ENV === APP_ENVIRONMENTS.DEV) {
    url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
    redirectUri = window.location.origin;
  }

  const uuid = getLocalStorage(DEVICE_ID);
  setCookie(FPID, uuid, 365);

  const accountBrand = getLocalStorage(CACHED_ACCOUNT_BRAND) ?? LOGIN_BRANDS.TELUS;

  const credentialData = {
    credentialsExtAuth: {
      deviceInfo: {
        deviceId: uuid,
        deviceIdType: "DEVICEID",
        deviceType: appProvider.AVS_PLATFORM,
        brand: accountBrand,
      },
      credentials: {
        redirect_uri: redirectUri,
        idp: ALTERNATE_IDENTITY_PROVIDER,
        email: email,
        password: password,
      },
    },
  };

  return request(
    {
      url: url,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: credentialData,
      withCredentials: true,
      returnResponseObject: true,
    },
    null,
    appProvider
  )
    .then((response) => {
      if (response?.data?.resultCode !== "KO") {
        // set the avs token expiry timestamp for a fresh login
        if (isExpiryFeatureEnabled()) {
          updateExpiryFromResultObj(response.data.resultObj);
        }

        // track dynatrace login event
        logDTEvent(
          DT_EVENTS.LOGIN,
          {
            [DT_ATTRIBUTES.USER_BRAND]: accountBrand, // or derive from response if needed
            [DT_ATTRIBUTES.DEVICE_ID]: uuid,
            [DT_ATTRIBUTES.LOGIN_METHOD]: LOGIN_METHODS.TTV_LOGIN,
            [DT_ATTRIBUTES.HAS_ERROR]: false,
            [DT_ATTRIBUTES.TV_ACCOUNT_ID]: response.data.resultObj?.profile?.profileData?.crmAccountId,
            [DT_ATTRIBUTES.USER_AVS_SESSION_ID]: getSessionStorage(ANALYTICS_STORAGE_KEYS.SESSION_ID),
            [DT_ATTRIBUTES.USER_PRODUCT_TYPE]:
              response.data.resultObj?.profile?.profileData?.properties?.[0]?.propertyName,
          },
          isDynatraceTrackingEnabled
        );
      }
      return response;
    })
    .catch((err) => {
      if (err) {
        logDTEvent(
          DT_EVENTS.LOGIN,
          {
            [DT_ATTRIBUTES.USER_BRAND]: accountBrand,
            [DT_ATTRIBUTES.DEVICE_ID]: uuid,
            [DT_ATTRIBUTES.LOGIN_METHOD]: LOGIN_METHODS.TTV_LOGIN,
            [DT_ATTRIBUTES.HAS_ERROR]: true,
            [DT_ATTRIBUTES.ERROR_CODE]: `ERR_${err.data?.errorDescription}`,
          },
          isDynatraceTrackingEnabled
        );
        return Promise.reject({
          url: err.config?.url,
          message: err.data?.message,
          type: "Server",
          code: err.data?.errorDescription,
        });
      }
    });
};

export default alternateLogin;
